import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './Navbar.css';
import { useTheme } from '../../context/ThemeContext';

const SunIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/>
    <line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/>
    <line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen]       = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled]       = useState(false);
  const { isDayMode, toggleTheme }        = useTheme();

  const openMenu  = () => setIsMenuOpen(true);
  const closeMenu = () => setIsMenuOpen(false);

  const handleNavClick = (sectionId) => {
    setActiveSection(sectionId);
    closeMenu();
    if (sectionId === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const el = document.getElementById(sectionId);
      if (el) window.scrollTo({ top: el.offsetTop - 90, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    document.body.classList.toggle('menu-open', isMenuOpen);
    return () => document.body.classList.remove('menu-open');
  }, [isMenuOpen]);

  useEffect(() => {
    if (isDayMode) {
      document.body.classList.add('day-mode');
    } else {
      document.body.classList.remove('day-mode');
    }
  }, [isDayMode]);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 50);
      if (window.scrollY < 100) { setActiveSection('home'); return; }
      const ids = ['home', 'services', 'portfolio', 'about', 'career', 'contact'];
      const pos = window.scrollY + 150;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && pos >= el.offsetTop && pos < el.offsetTop + el.offsetHeight) {
          setActiveSection(id);
          return;
        }
      }
    };
    let ticking = false;
    const listener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => { onScroll(); ticking = false; });
        ticking = true;
      }
    };
    window.addEventListener('scroll', listener, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', listener);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeMenu(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const navLinks = ['home', 'services', 'portfolio', 'about', 'career'];

  /* ── Inline styles — guaranteed, no CSS class can override ── */
  const drawerStyle = {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '78%',
    maxWidth: '300px',
    height: '100dvh',
    background: isDayMode ? 'rgba(247,250,255,0.99)' : 'rgba(9,14,30,0.98)',
    borderLeft: isDayMode ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(34,211,238,0.14)',
    boxShadow: '-14px 0 50px rgba(0,0,0,0.7)',
    zIndex: 1100,
    display: isMenuOpen ? 'flex' : 'none',
    flexDirection: 'column',
    transform: isMenuOpen ? 'translateX(0)' : 'translateX(100%)',
    transition: 'transform 0.38s cubic-bezier(0.4,0,0.2,1)',
    overflow: 'hidden',  /* drawer itself does NOT scroll */
  };

  const drawerHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.1rem 1.5rem',
    borderBottom: isDayMode ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(34,211,238,0.12)',
    flexShrink: 0,
    minHeight: '64px',
  };

  const drawerNavStyle = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '0.5rem 0 0',
  };

  /* Footer is flex-shrink:0 so it never gets pushed off screen */
  const drawerFooterStyle = {
    flexShrink: 0,
    borderTop: isDayMode ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(34,211,238,0.12)',
    padding: '1rem 1.5rem 1.5rem',
    display: 'flex',
    flexDirection: 'column',
  };

  /* Theme button — plain row, no circle, no fixed size */
  const themeBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.65rem',
    background: 'none',
    border: 'none',
    padding: '0.4rem 0',
    margin: 0,
    cursor: 'pointer',
    color: isDayMode ? '#0a0e27' : '#22d3ee',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: '0.95rem',
    fontWeight: 500,
    lineHeight: 1,
    WebkitTapHighlightColor: 'transparent',
    width: 'auto',
    height: 'auto',
    borderRadius: 0,
  };

  const drawerPortal = ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`navbar-overlay${isMenuOpen ? ' active' : ''}`}
        onClick={closeMenu}
      />

      {/* Drawer — all layout via inline styles, guaranteed */}
      <div style={drawerStyle}>

        {/* Header */}
        <div style={drawerHeaderStyle}>
          <a
            href="#home"
            className="drawer-logo-text"
            onClick={(e) => { e.preventDefault(); handleNavClick('home'); }}
          >
            OPRAMIX
          </a>
          <button className="mobile-close-btn" onClick={closeMenu} aria-label="Close menu">
            <CloseIcon />
          </button>
        </div>

        {/* Nav links — this part scrolls if needed */}
        <nav style={drawerNavStyle}>
          {navLinks.map((section) => (
            <a
              key={section}
              href={`#${section}`}
              className={`navbar-item${activeSection === section ? ' active' : ''}`}
              onClick={(e) => { e.preventDefault(); handleNavClick(section); }}
            >
              {section === 'about'
                ? 'About Us'
                : section.charAt(0).toUpperCase() + section.slice(1)}
            </a>
          ))}

          {/* Contact button */}
          <a
            href="#contact"
            className={`navbar-item navbar-item-contact${activeSection === 'contact' ? ' active' : ''}`}
            onClick={(e) => { e.preventDefault(); handleNavClick('contact'); }}
          >
            Contact
          </a>
        </nav>

        {/* Footer — flex-shrink:0, ALWAYS visible at bottom */}
        <div style={drawerFooterStyle}>
          <button
            style={themeBtnStyle}
            onClick={toggleTheme}
            aria-label={isDayMode ? 'Switch to Night Mode' : 'Switch to Day Mode'}
          >
            <span style={{ display:'flex', alignItems:'center', flexShrink:0, width:20, height:20 }}>
              {isDayMode
                ? <MoonIcon />
                : <SunIcon />}
            </span>
            <span style={{ color:'inherit' }}>
              {isDayMode ? 'Night Mode' : 'Day Mode'}
            </span>
          </button>
        </div>

      </div>
    </>,
    document.body
  );

  return (
    <>
      <nav className={`navbar${isScrolled ? ' scrolled' : ''}${isDayMode ? ' day-mode' : ''}`}>
        <div className="navbar-container">

          <a href="#home" className="navbar-logo"
            onClick={(e) => { e.preventDefault(); handleNavClick('home'); }}>
            <span className="logo-text">OPRAMIX</span>
          </a>

          <div className="navbar-menu">
            {navLinks.map((section) => (
              <a
                key={section}
                href={`#${section}`}
                className={`navbar-item${activeSection === section ? ' active' : ''}`}
                onClick={(e) => { e.preventDefault(); handleNavClick(section); }}
              >
                {section === 'about'
                  ? 'About Us'
                  : section.charAt(0).toUpperCase() + section.slice(1)}
              </a>
            ))}
            <a
              href="#contact"
              className={`navbar-item navbar-item-contact${activeSection === 'contact' ? ' active' : ''}`}
              onClick={(e) => { e.preventDefault(); handleNavClick('contact'); }}
            >
              Contact
            </a>
          </div>

          <div className="navbar-right-controls">
            <button
              className="theme-toggle-btn theme-toggle-desktop"
              onClick={toggleTheme}
              title={isDayMode ? 'Switch to Night Mode' : 'Switch to Day Mode'}
              aria-label={isDayMode ? 'Switch to Night Mode' : 'Switch to Day Mode'}
            >
              {isDayMode ? <MoonIcon /> : <SunIcon />}
            </button>

            <button
              className="navbar-hamburger"
              onClick={openMenu}
              aria-label="Open navigation menu"
              aria-expanded={isMenuOpen}
            >
              <span className="hamburger-line" />
              <span className="hamburger-line" />
              <span className="hamburger-line" />
            </button>
          </div>

        </div>
      </nav>

      {drawerPortal}
    </>
  );
};

export default Navbar;
